import { AdService } from '..'
import { IDOMConf } from '../../../domain/conf'
import { Entity } from '../../../domain/entity'
import { Utils } from '../../../utils/common'
import { DeviceTypes, DOMUtils } from '../../../utils/dom'
import { AdTarget, IAdTargets } from '../targeting/target'
import { AdEvents } from './events'

// require styles (webpack)
import './styles.css' // tslint:disable-line: no-import-side-effect

export type AdSize = number[]
export enum AdType {
    HTML = 'html',
    VIDEO = 'video'
}

export interface IAdRefreshConf {
    /**
     * Maximum number of times ads will be refreshed.
     */
    max?: number

    /**
     * Seconds between refreshes.
     */
    s?: number
}

export interface IAdLazyLoadConf {
    /**
     * 100 would mean fetch slots within 1 viewport
     * 500 would mean fetch slots within 5 viewports
     */
    fetchMarginPercent: number

    /**
     * 100 would mean render slots within 1 viewport
     * 500 would mean render slots within 5 viewports
     */
    renderMarginPercent: number

    /**
     * 2.0 would mean double the above values on mobile
     * where viewports are smaller and users scroll faster
     */
    mobileScaling: number
}

export interface IAdRefreshDOMConf extends IDOMConf {
    attr: {
        /**
         * Enables/disables refresh.
         */
        state: {
            key: 'data-ad-ref'
            value?: {
                off: 'off'
            }
        }
    }
}

export const AdRefreshDOM: IAdRefreshDOMConf = {
    attr: {
        state: {
            key: 'data-ad-ref',
            value: {
                off: 'off'
            }
        }
    }
}

export interface IAdDOMConf extends IDOMConf {
    attr: {
        /**
         * Main identifier. Optionally accepts (JSON) `AdSizes`.
         */
        size: {
            key: 'data-ad'
            value?: string
        }

        /**
         * Identifies ads that should be removed if adLight is active.
         */
        adLightIneligible: {
            key: 'data-ad-light-ineligible'
        }

        /**
         * For ad-level targeting to distinguish between multiple of the same size ad.
         */
        location: {
            key: 'data-ad-loc'
            value?: string
        }

        /**
         * For mobile ad sizes.
         */
        mobileSize: {
            key: 'data-ad-mobile'
        }

        /**
         * For tablet ad sizes.
         */
        tabletSize: {
            key: 'data-ad-tablet'
        }

        /**
         * Identifies ad as out-of-page.
         */
        outOfPage: {
            key: 'data-ad-oop'
        }

        /**
         * Identifies comments ad.
         */
        commentsAd: {
            key: 'data-ad-comment'
        }

        /**
         * Identifies approved acceptable ads slots.
         */
        acceptableAd: {
            key: 'data-ad-acceptable'
        }

        /**
         * Identifies ad as data-ad-high-impact.
         */
        highImpact: {
            key: 'data-ad-high-impact'
        }

        /**
         * Ad type (for eventual use with video ads?). Currently just 'html'.
         */
        target: {
            key: 'data-ad-target'
            value?: string
        }

        /**
         * Ad type (for eventual use with video ads?). Currently just 'html'.
         */
        type: {
            key: 'data-ad-type'
            value?: {
                html: AdType.HTML
                video: AdType.VIDEO
            }
        },

        /**
         * Ad type for getting DOM width of element
         */
        width: {
            key: 'data-ad-width'
            value?: string
        }
    }
}

export const AdDOM: IAdDOMConf = {
    attr: {
        acceptableAd: { key: 'data-ad-acceptable' },
        adLightIneligible: { key: 'data-ad-light-ineligible' },
        commentsAd: { key: 'data-ad-comment' },
        highImpact: { key: 'data-ad-high-impact' },
        location: { key: 'data-ad-loc' },
        mobileSize: { key: 'data-ad-mobile' },
        outOfPage: { key: 'data-ad-oop' },
        size: { key: 'data-ad' },
        tabletSize: { key: 'data-ad-tablet' },
        target: { key: 'data-ad-target' },
        type: {
            key: 'data-ad-type',
            value: {
                html: AdType.HTML,
                video: AdType.VIDEO
            }
        },
        width: { key: 'data-ad-width' }
    }
}
export interface IAd {
    advertiser?: number
    el: Element
    id: string
    sizes: AdSize[]
    mobileSizes?: AdSize[]
    sizeRendered?: AdSize
    slot?: any
    targets?: AdTarget[]
    tabletSizes?: AdSize[]
    type: AdType
    width: number

    destroy(): void
    display(size: AdSize, advertiser: number): void
    fallBack(content: string): void
    getMaxHeight(): number
    isAdLightIneligible(): boolean
    isHighImpact(): boolean
    isOutOfPage(): boolean
    isWallpaper(): boolean
    isNative(): boolean
    isInterscroller(): boolean
    calculateRefreshSizes(el: Element): void
    loaded(): void
    validSizes(): AdSize[]
    validSizesWithFluid(): Array<AdSize | 'fluid'>
}

export class Ad extends Entity implements IAd {
    public static defaultConf = {
        size: [300, 250], // tslint:disable-line: no-magic-numbers
        type: AdType.HTML
    }

    public advertiser?: number
    public id: string
    public sizes: AdSize[]
    public mobileSizes?: AdSize[]
    public sizeRendered?: AdSize
    public targets?: AdTarget[]
    public tabletSizes?: AdSize[]
    public type: AdType
    public width: number
    public reflowHeight: string

    public adClasses = {
        centerRefreshAd: 'ad-refresh',
        hidden: 'visually-hidden',
        refreshBkgd: 'ad-refresh-bkgd',
        stickyWidget: 'sticky-widget',
        widgetSticky: 'widget-sticky'
    }

    /**
     * Initializes the ad.
     *
     * @param {Element} el
     */
    public constructor(
        public el: Element,
        public targeting?: (targets: IAdTargets, loc?: string) => AdTarget[],
        public refresh: boolean | IAdRefreshConf = false,
        public adLight = false,
        public pagetype = ''
    ) {
        super()

        // require id
        if (!el.hasAttribute('id')) {
            el.setAttribute('id', `ad-${Utils.randomIdentifierGenerator()}`)
        }
        this.id = el.getAttribute('id')
        this.width = el.getBoundingClientRect().width

        // recalculate for refresh
        if (this.refresh &&
            !this.adLight &&
            !this.isNative() &&
            !this.isHighImpact() &&
            !this.hasRefreshEnable()) {
            this.calculateRefreshSizes(this.el)
            // set ad height (moved from calculateRefreshSizes)
            // DOMUtils.style(el, DOMUtils.attrs.height, this.reflowHeight)
            el.setAttribute('style', `height:${this.getMaxHeight()}px`)
        }

        // set size
        if (!this.isOutOfPage()) {
            this.sizes = Ad.normalizeSizes(
                JSON.parse(el.getAttribute(AdDOM.attr.size.key) || 'null') || Ad.defaultConf.size)
        }

        // set targeting
        if (targeting) {
            this.targets = this.targeting(
                JSON.parse(el.getAttribute(AdDOM.attr.target.key) || 'null'),
                el.getAttribute(AdDOM.attr.location.key))
        }

        this.type = el.getAttribute(AdDOM.attr.type.key) as AdType || Ad.defaultConf.type
    }

    /**
     * Retrieve all ads from DOM.
     */
    public static getAdElements(): Node[] {
        return DOMUtils.nodes(document.querySelectorAll(`[${AdDOM.attr.size.key}]`))
    }

    /**
     * Normalizes sizes (can be single or nested array of width/height values).
     */
    public static normalizeSizes(sizes: number[] | number[][]): AdSize[] {
        if (!(sizes && sizes.length)) {
            return undefined
        }
        return ((Array.isArray(sizes[0]) ? sizes : [sizes]) as AdSize[])
    }

    /**
     * calculateRefreshSizes for ads that refresh
     */
    public calculateRefreshSizes(el: Element) {

        if (!this.refresh) {
            return
        }

        const minimumAdHeight = 20
        const adSizes = this.getAdDeviceTypeSizes()

        // get the integer ad heights
        const adHeights = adSizes.map((size: any) => {
            return size.length > 1 && !isNaN(size[1]) ? parseInt(size[1], 10) : 0
        })

        // get the largest height, or zero
        const adHeightMax = adHeights.length > 0 ? Math.max(...adHeights) : 0

        if (isFinite(adHeightMax) && adHeightMax >= minimumAdHeight) {
            this.reflowHeight = `${adHeightMax}px !important`
            this.centerRefreshAds(el)
        }
    }

    /**
     * Removes elements not handled by `adapter.destroy`.
     */
    public destroy(): void {
        //
    }

    /**
     * Called when ad is served (before creative loads).
     *
     * @param {AdSize} size actual size of ad (will be one of requested)
     */
    public display(size?: AdSize, advertiser?: number): void {
        this.advertiser = advertiser
        this.sizeRendered = size
    }

    /**
     * Displays fallback ad.
     */
    public fallBack(s: string): void {
        this.el.innerHTML = s
    }

    public isOutOfPage(): boolean {
        return this.el.hasAttribute(AdDOM.attr.outOfPage.key)
    }

    /**
     * Checks if the ad has Ad-Light InEligible.
     */
    public isAdLightIneligible(): boolean {
        return this.el.hasAttribute(AdDOM.attr.adLightIneligible.key)
    }

    public isHighImpact(): boolean {
        return this.el.hasAttribute(AdDOM.attr.highImpact.key)
    }

    public isCommentsAd(): boolean {
        return this.el.hasAttribute(AdDOM.attr.commentsAd.key)
    }

    /**
     * Checks if ad is a wallpaper.
     */
    public isWallpaper(): boolean {
        return JSON.stringify(this.sizes) === JSON.stringify([AdService.Wallpaper.size])
    }

    /**
     * Checks if ad is a leaderboard.
     */
    public isLeaderBoard(): boolean {
        return this.el.id.search('leaderboard') > -1
    }

    /**
     * Checks if acceptable ad.
     */
    public isAcceptable(): boolean {
        return this.el.hasAttribute(AdDOM.attr.acceptableAd.key)
    }

    /**
     * Checks if the ad has a interscroller size.
     */
    public isInterscroller(): boolean {
        for (const _ of AdService.Interscroller.sizes) {
            if (JSON.stringify(this.el.getAttribute(AdDOM.attr.size.key))
                .indexOf(JSON.stringify(_)) > -1) {
                return true
            }
        }
        return false
    }

    /**
     * Checks if ad is a native.
     */
    public isNative(): boolean {
        return JSON.stringify(this.el.getAttribute(AdDOM.attr.size.key))
            .indexOf(`${AdService.Native.size}`) > -1
    }

    /**
     * Checks if ad has data-ad-ref flag enable.
     */
    public hasRefreshEnable(): boolean {
        return this.el.hasAttribute(AdRefreshDOM.attr.state.key)
    }

    /**
     * Checks if ad has data-ad-mobile sizes.
     */
    public hasMobileSize(): boolean {
        return this.el.hasAttribute(AdDOM.attr.mobileSize.key)
    }

    /**
     * Checks if ad has data-ad-tablet sizes.
     */
    public hasTabletSize(): boolean {
        return this.el.hasAttribute(AdDOM.attr.tabletSize.key)
    }

    /**
     * Called when iframe containing ad creative loads (creative itself may still be loading).
     */
    public loaded(): void {
        this.el.setAttribute(AdDOM.attr.width.key, `${this.el.getBoundingClientRect().width}`)
        this.el.dispatchEvent(Utils.event(AdEvents.DISPLAY, this))
    }

    /**
     * Filters sizes whose width(s) fit within container element.
     */
    public validSizes(): AdSize[] {

        // wallpaper is excluded
        if (this.isWallpaper()) {
            return this.sizes
        }

        // leaderboard is included
        if (this.isLeaderBoard()) {
            return this.sizes
        }

        if (this.isCommentsAd()) {
            return this.sizes
        }

        // Get rendered DOM width
        const renderedWidth = this.el.getBoundingClientRect().width

        // Get width that was passed to attribute when ad was loaded
        /*tslint:disable:radix*/
        const widthAttr = this.el.getAttribute(AdDOM.attr.width.key)
        const recordedWidth = widthAttr ? parseInt(widthAttr) : 0
        /*tslint:enable*/

        // Check if rendered width > 0 ...
        // If 0, means the element was removed from the DOM at the time of checking (sticky/refresh)
        const width = renderedWidth > 0 ? renderedWidth : recordedWidth

        // Check if the ad has mobile attributes
        this.mobileSizes = this.getMobileSizes()

        // Check if the ad has tablet attributes
        this.tabletSizes = this.getTabletSizes()

        // Return comparison, with check for big box
        // If big box, add 1 to width because column border removes 1px when calculating element width
        return (
            this.getAdDeviceTypeSizes() || []).filter((size) => {
                return size[0] <= (this.isBigBox() ? width + 1 : width)
            })
    }

    public validSizesWithFluid(): Array<AdSize | 'fluid'> {
        // 8x8 ads are treated as 'fluid' size
        const fluid = 8
        return this.validSizes().map((size) => {
            return size[0] === fluid && size[1] === fluid ? 'fluid' : size
        })
    }

    public getMaxHeight(): number {
        const sizes = this.validSizes()
        if (sizes) {
            return sizes.reduce((acc, cur) => Math.max(acc, cur[1]), 0)
        }
        return 0
    }

    /**
     * Returns ad slot array sizes depending on device type
     */
    public getAdDeviceTypeSizes(): AdSize[] {
        switch (DOMUtils.deviceType()) {
            case DeviceTypes.MOBILE:
                return this.getMobileSizes()
            // case DeviceTypes.TABLET:  // not currently supported
            //     return this.getTabletSizes()
            default:
                return this.getDesktopSizes()
        }
    }

    /**
     * Get Desktop ad sizes from DOM element
     */
    public getDesktopSizes() {
        const desktopSizes = Ad.normalizeSizes(JSON.parse(this.el.getAttribute(AdDOM.attr.size.key)))
        return (desktopSizes === undefined) ? [] : desktopSizes
    }

    /**
     * Get Mobile ad sizes from DOM element
     */
    public getMobileSizes() {
        const mobileSizes = Ad.normalizeSizes(JSON.parse(this.el.getAttribute(AdDOM.attr.mobileSize.key)))
        return (mobileSizes === undefined) ? [] : mobileSizes
    }

    /**
     * Get Tablet ad sizes from DOM element
     */
    public getTabletSizes() {
        const tabletSizes = Ad.normalizeSizes(JSON.parse(this.el.getAttribute(AdDOM.attr.tabletSize.key)))
        return (tabletSizes === undefined) ? [] : tabletSizes
    }

    /**
     * Determine if ad is big box (within a column that may alter width)
     */
    public isBigBox(): boolean {
        return this.el.id.search('bigbox') > -1
    }

    /**
     * Center leaderboard ad on refresh
     */
    public centerRefreshAds(el: Element) {
        const hasStickyWidgetClass = document.querySelector(`.${this.adClasses.stickyWidget}`) !== null
        const hasWidgetStickyClass = document.querySelector(`.${this.adClasses.widgetSticky}`) !== null

        if (this.isLeaderBoard() || (!hasStickyWidgetClass && !hasWidgetStickyClass)) {
            el.classList.add(this.adClasses.centerRefreshAd)
        } else if (hasWidgetStickyClass) {
            el.classList.add(this.adClasses.widgetSticky)
        } else if (hasStickyWidgetClass) {
            el.classList.add(this.adClasses.stickyWidget)
        }
    }
}
